import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, CircularProgress, Grid, IconButton, Paper, TextField, Tooltip } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Edit } from '@mui/icons-material'
import { useParams } from 'react-router'
import { enqueueSnackbar } from 'notistack'
import { globalGetService, globalPutService } from '../../../utils/globalApiServices'
import { checkApiStatus } from '../../../utils'
import { getCalenderListApi } from '../../settings/calender/apiService'
import { getBusinessDaysApi, updateBusinessDaysApi } from '../apiService'
import { PageLoader } from '../../../shared_elements'
import { trackActivity } from '../../../utils/mixpanel'
import ModuleWiseRevisionHistory from '../../../shared_elements/components/ModuleWiseRevisionHistory'
import RevisionHistory from '../../../shared_elements/components/RevisionHistory'
import { PermissionWrapper } from '../../../wrappers'

const viewModeAutocompleteSx = {
  '& .MuiInputBase-input.Mui-disabled': { color: '#969696', opacity: 1 },
  '& .MuiChip-root.Mui-disabled': { opacity: 1, backgroundColor: '#e2e2e2', '& .MuiChip-label': { color: '#969696' } },
}

const BusinessDaysConfig = () => {
  const params = useParams()

  const [isLoading, setLoading] = useState(false)
  const [modeType, setModeType] = useState('view')
  const [calenderList, setCalenderList] = useState([])
  const [data, setData] = useState([])
  const prevDataRef = useRef([])
  const [revisionHistory, setRevisionHistory] = useState({ modal: false, data: null })

  const onFieldChange = (keyParam, value) => {
    setData(value)
  }

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: 'Business Days', page_type: page_type })
  }

  useEffect(() => {
    getCalenderList()
    getBusinessDaysData()
  }, [])

  const getCalenderList = () => {
    setLoading(true)
    globalGetService(getCalenderListApi()).then((response) => {
      if (checkApiStatus(response)) {
        setCalenderList(response?.data?.data?.list)
      }
      setLoading(false)
    })
  }

  const getBusinessDaysData = () => {
    setLoading(true)
    globalGetService(getBusinessDaysApi(params)).then((response) => {
      if (checkApiStatus(response)) {
        setData(response?.data?.data?.business_days)
      }
      setLoading(false)
    })
  }

  const onUpdateBusinessDays = () => {
    setLoading(true)
    const payload = { business_days: data?.map((item) => item?.id) }
    globalPutService(updateBusinessDaysApi(params), payload).then((response) => {
      if (checkApiStatus(response)) {
        enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
        trackActivity('Item Edited', { page_title: 'Edit Business Days', success_msg: response?.data?.message, payload, response: response?.data })
        setModeType('view')
        getBusinessDaysData()
      }
      setLoading(false)
    })
  }

  return (
    <>
      <Paper elevation={0} sx={{ padding: '10px', minHeight: '60px', alignContent: 'center', justifyContent: 'center', boxShadow: '0px 0px 2px 1px #BBBBBB' }}>
        <Grid container columnSpacing={1}>
          <Grid size={{ md: 11, xs: 6 }}>
            <Autocomplete
              disablePortal
              disabled={modeType === 'view'}
              multiple
              slotProps={{ chip: { size: 'small', sx: { height: 24, fontSize: '12px' } } }}
              options={calenderList || []}
              getOptionLabel={(option) => option?.name || ''}
              noOptionsText="No Option Available"
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={data || []}
              onChange={(_, newValue) => onFieldChange('business_days', newValue)}
              renderInput={(renderParams) => {
                const selectedLabel = renderParams?.inputProps?.value || ''
                return (
                  <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ''} placement="bottom" arrow>
                    <TextField
                      {...renderParams}
                      label="Business Days"
                      variant="outlined"
                      size="small"
                      margin="normal"
                      fullWidth
                      helperText=""
                      error={false}
                      slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                          ...renderParams?.InputProps,
                          endAdornment: (
                            <>
                              {false ? <CircularProgress color="inherit" size={20} /> : null}
                              {renderParams?.InputProps?.endAdornment}
                            </>
                          ),
                        },
                      }}
                      placeholder={data?.length > 0 ? '' : 'Select Business Days'}
                      sx={modeType === 'view' ? viewModeAutocompleteSx : {}}
                    />
                  </Tooltip>
                )
              }}
            />
          </Grid>
          <Grid size={{ md: 1, xs: 12 }} className="table-icon-container" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "calendar", operationType: ['U'] }}>
              {modeType === 'view' && (
              <Tooltip title="Edit">
                <Edit
                  onClick={() => { setModeType('edit'); prevDataRef.current = data; handleItemClicked('Edit Icon - BusinessDaysConfig') }}
                  fontSize="small"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            </PermissionWrapper>
            {modeType === 'view' && (
              <Box sx={{ display: 'flex', alignItems: 'center', '& .revision-history': { top: '0 !important' } }}>
                <Tooltip title="Revision History">
                  <ModuleWiseRevisionHistory
                    ViewGlobalHistory={() => { setRevisionHistory({ modal: true, data: { slug: params?.leaseSlug, type: 'section' } }); handleItemClicked('Revision History Icon - BusinessDaysConfig') }}
                  />
                </Tooltip>
              </Box>
            )}
            {modeType === 'edit' && (
              <>
                <Tooltip title="Save">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => { onUpdateBusinessDays(); handleItemClicked('Save Icon - BusinessDaysConfig') }}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ bgcolor: '#e3f2fd', marginRight: '5px' }}
                  >
                    <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => { setModeType('view'); setData(prevDataRef.current); handleItemClicked('Cancel Icon - BusinessDaysConfig') }}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ bgcolor: '#ffebee' }}
                  >
                    <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
      {revisionHistory.modal && (
        <RevisionHistory
          moduleName="lease"
          section_type="business_days"
          revisionHistory={revisionHistory}
          onToggleModal={() => setRevisionHistory({ modal: false, data: null })}
        />
      )}
      {isLoading && <PageLoader />}
    </>
  )
}

export default BusinessDaysConfig
